import { useState, useCallback, useEffect } from 'react';
import { SchoolStructureData } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  getSchoolStructure,
  updateSchoolStructure
} from '../../../apis/schoolStrcuture';
import { SnackbarSeverityType } from '../../../enums';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schoolStructureFormSchema } from './yupSchema';
import formUtils from '../../../utils/formUtils';

export const useSchoolStructureForm = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<SchoolStructureData>({
    resolver: yupResolver(schoolStructureFormSchema),
    mode: 'onChange',
    defaultValues: {
      school: loggedInUser?.school,
      houses: [],
      sections: {},
      newSectionName: ''
    }
  });

  const formData = watch();

  const [expandedSection, setExpandedSection] = useState<string | false>(
    'houses'
  );
  const [isLoading, setIsLoading] = useState(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const dispatch: AppDispatch = useDispatch();

  const fetchSchoolStructure = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: any = await getSchoolStructure(loggedInUser?.school);
      reset(response);
    } catch (error: any) {
      reset();
    } finally {
      setIsLoading(false);
    }
  }, [loggedInUser?.school, reset]);

  useEffect(() => {
    if (loggedInUser?.school) {
      fetchSchoolStructure();
    }
  }, [loggedInUser?.school, dispatch, fetchSchoolStructure]);

  const handleAccordionChange = useCallback(
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSection(isExpanded ? panel : false);
    },
    []
  );

  const onSubmit = useCallback(
    async (data: SchoolStructureData) => {
      const { newSectionName, ...rest } = data;
      try {
        const response = await updateSchoolStructure(
          loggedInUser?.school,
          rest
        );
        if (response) setToastOpen(true);
        setToastSeverity(SnackbarSeverityType.success);
        setToastMessage('School Structure updated successfully!');
      } catch (error: any) {
        setToastOpen(true);
        setToastSeverity(SnackbarSeverityType.error);
        setToastMessage(
          error.errors[0].message ??
            'Failed to update school. Please try again.'
        );
      }
    },
    [loggedInUser?.school]
  );

  const onError = useCallback((formErrors: typeof errors) => {
    const firstFormError = formUtils.findFirstFormError(formErrors);
    setExpandedSection(firstFormError?.fieldPath.split('.')[0] || '');

    setToastOpen(true);
    setToastSeverity(SnackbarSeverityType.error);

    setToastMessage(
      firstFormError?.errorMessage ||
        (firstFormError?.fieldPath
          ? `Please correct the error in "${firstFormError.fieldPath}".`
          : 'Please correct the errors in the form.')
    );
  }, []);

  return {
    control,
    handleSubmit: handleSubmit(onSubmit, onError),
    formData,
    expandedSection,
    isLoading,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleAccordionChange,
    setValue,
    setToastOpen
  };
};
